// src/components/SetterTab.jsx
import React, { useState } from "react";
import axios from "axios";
import MCQSetter from "./MCQSetter";
import {
  CodeBracketIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";

const SetterTab = ({ selectedTopic }) => {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem("setter_mode") || "code";
  }); // 'code' | 'mcq'

  const [fileName, setFileName] = useState(() => {
    return localStorage.getItem("setter_code_filename") || "";
  });
  const [code, setCode] = useState(() => {
    return localStorage.getItem("setter_code_content") || "";
  });
  const [message, setMessage] = useState("");

  const backendURL = process.env.REACT_APP_BACKEND_URL;

  const handleModeChange = (newMode) => {
    setMode(newMode);
    localStorage.setItem("setter_mode", newMode);
  };

  const handleFileNameChange = (val) => {
    setFileName(val);
    localStorage.setItem("setter_code_filename", val);
  };

  const handleCodeChange = (val) => {
    setCode(val);
    localStorage.setItem("setter_code_content", val);
  };

  const setCodeHandler = async () => {
    if (!fileName || !code) {
      setMessage("Both filename and code are required.");
      return;
    }

    try {
      setMessage("Saving code...");

      const response = await axios.post(`${backendURL}/save_code`, {
        filename: fileName,
        code: code,
        topic: selectedTopic,
      });

      if (response.status === 200) {
        setMessage("Code saved successfully.");
        // Notify FilesTab and update sessionStorage cache
        try {
          const cacheKey = `codetyper_files_cache_${selectedTopic}`;
          const raw = sessionStorage.getItem(cacheKey);
          const parsed = raw ? JSON.parse(raw) : {};
          parsed[fileName] = code;
          sessionStorage.setItem(cacheKey, JSON.stringify(parsed));
        } catch (e) {}

        window.dispatchEvent(
          new CustomEvent("codetyper:file_saved", {
            detail: { topic: selectedTopic, filename: fileName, code },
          })
        );

        clearInputs();
      } else {
        setMessage("Failed to save code.");
      }
    } catch (error) {
      console.error(error);
      setMessage("Error saving code.");
    }
  };

  const clearInputs = () => {
    setFileName("");
    setCode("");
    localStorage.removeItem("setter_code_filename");
    localStorage.removeItem("setter_code_content");
  };

  return (
    <div className="flex justify-center bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white min-h-[calc(100vh-5rem)] pb-12 px-2 sm:px-6">
      {/* Setter Container */}
      <div className="w-full space-y-4">
        {/* Top Header & Mode Switcher Pills */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-gray-200/80 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              Setter
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Select setter mode to create or update files
            </p>
          </div>

          {/* Mode Switcher */}
          <div className="flex bg-gray-100 dark:bg-gray-700/80 p-1 rounded-xl gap-1 border border-gray-200 dark:border-gray-600">
            <button
              type="button"
              onClick={() => handleModeChange("code")}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                mode === "code"
                  ? "bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-sm"
                  : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              <CodeBracketIcon className="w-4 h-4" />
              <span>Code Editor</span>
            </button>

            <button
              type="button"
              onClick={() => handleModeChange("mcq")}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                mode === "mcq"
                  ? "bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-sm"
                  : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              <ListBulletIcon className="w-4 h-4" />
              <span>MCQ Setter</span>
            </button>
          </div>
        </div>

        {/* Content based on Active Mode */}
        {mode === "code" ? (
          <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 space-y-6 border border-gray-200/80 dark:border-gray-700">
            {/* File Name Input */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-1 text-sm font-semibold">
                File Name:
              </label>
              <input
                type="text"
                value={fileName}
                onChange={(e) => handleFileNameChange(e.target.value)}
                className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500 placeholder-gray-400 dark:placeholder-gray-400 transition-colors"
                placeholder="Enter filename"
              />
            </div>

            {/* Code Input */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-1 text-sm font-semibold">
                Code:
              </label>
              <textarea
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                rows="10"
                onInput={(e) => {
                  // Auto-resize the textarea based on content
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500 placeholder-gray-400 dark:placeholder-gray-400 resize-none text-gray-900 dark:text-white transition-colors"
                placeholder="Enter code here..."
              ></textarea>
            </div>

            {/* Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={setCodeHandler}
                className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 dark:bg-purple-600 dark:hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-colors"
              >
                Set Code
              </button>
              <button
                onClick={clearInputs}
                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
              >
                Clear
              </button>
            </div>

            {/* Message Display */}
            {message && (
              <div className="mt-4 p-3 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                {message}
              </div>
            )}
          </div>
        ) : (
          <MCQSetter selectedTopic={selectedTopic} />
        )}
      </div>
    </div>
  );
};

export default SetterTab;
